import { useState } from 'react';
import { Segmented } from '../components/data';
import {
  Avatar,
  DeleteButton,
  Empty,
  EmployeePicker,
  ErrorBanner,
  Field,
  Loading,
  Modal,
  PageHead,
} from '../components/ui';
import { api } from '../lib/api';
import { maturityName } from '../lib/format';
import { useResource } from '../lib/hooks';
import type {
  Employee,
  MeasurableComparison,
  Process,
  ProcessArea,
  ProcessMeasurable,
  ProcessMeasurableOption,
} from '../lib/types';
import { formatMeasure } from './eos/eosUi';

/**
 * Processes — what the business actually does.
 *
 * Brought across from the Processes page of the standalone EOS app. Each
 * process carries the person accountable for it, the areas that run it,
 * the maturity level it belongs to, and the measurables that say whether it is
 * working. Grouped by business area, because "what does Service Delivery own"
 * is the question this page gets asked.
 *
 * Admin-only, in Settings alongside Employees: this is the shape of the
 * business rather than a working document. The API enforces the same rule —
 * see server.ts.
 *
 * Two things read differently here from the app it came from, both because
 * this estate already has an answer:
 *
 *   - PROCESSES ARE FILED BY BUSINESS AREA — the thirteen functions on the
 *     dashboard — rather than by that app's own department headings, only one
 *     of which exists in this estate's department table. See migration 065.
 *   - THE LEVEL is the OML ladder this app already scores against, worded the
 *     way the OML Assessment words it. A second five-level vocabulary would
 *     make "level 3" mean two things across two pages.
 */

/** Where a process filed under no area goes. Sorts last — see `ordered`. */
const UNFILED = 'Unfiled';

const GOAL_GLYPH: Record<MeasurableComparison, string> = { '>=': '≥', '<=': '≤', '=': '=' };

/** `≥ 95%` — a measurable's goal, written the way the scorecard writes it. */
function goalLabel(m: Pick<ProcessMeasurable, 'Goal' | 'Comparison' | 'Unit'>): string {
  if (m.Goal === null) return '';
  return `${GOAL_GLYPH[m.Comparison]} ${formatMeasure(Number(m.Goal), m.Unit)}`;
}

/** `L3 Defined`. Deliberately unbanded — see the note on the filter below. */
function LevelBadge({ level }: { level: number }) {
  return (
    <span className="badge badge-neutral" title={`Operational maturity level ${level}`}>
      L{level} {maturityName(level)}
    </span>
  );
}

export function ProcessesPage() {
  const processes = useResource<Process[]>('/processes');
  const areas = useResource<ProcessArea[]>('/processes/areas');
  const employees = useResource<Employee[]>('/employees?status=active');

  const [editing, setEditing] = useState<Process | null>(null);
  const [creating, setCreating] = useState(false);
  /* 'all', a level as a string, or 'none' for the unrated. Unrated is its own
     filter rather than a sixth level: "nobody has decided yet" is a different
     fact from "this belongs at level 1", and it is the one worth finding. */
  const [levelFilter, setLevelFilter] = useState('all');
  const [error, setError] = useState<string | null>(null);

  const all = processes.data ?? [];

  const remove = async (p: Process) => {
    setError(null);
    try {
      await api.del(`/processes/${p.ProcessId}`);
      processes.reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not delete that process.');
    }
  };

  const countAt = (id: string) =>
    id === 'all'
      ? all.length
      : all.filter((p) => (id === 'none' ? p.MaturityLevel === null : p.MaturityLevel === Number(id)))
          .length;

  /* Levels nothing sits at are left out. A filter offering five buttons that
     all produce an empty page is noise on a list of a dozen processes. */
  const filterOptions = [
    { id: 'all', label: `All ${all.length}` },
    ...[1, 2, 3, 4, 5]
      .filter((level) => countAt(String(level)) > 0)
      .map((level) => ({ id: String(level), label: `L${level} ${countAt(String(level))}` })),
    ...(countAt('none') > 0 ? [{ id: 'none', label: `Not rated ${countAt('none')}` }] : []),
  ];

  /* Deleting the last process at a level takes its button off the filter with
     it. Falling back to All rather than leaving the filter pointing at a bucket
     that is no longer offered — an empty page with nothing highlighted, which
     reads as the page having broken rather than as a filter still applied. */
  const active = filterOptions.some((o) => o.id === levelFilter) ? levelFilter : 'all';

  const shown =
    active === 'all'
      ? all
      : all.filter((p) =>
          active === 'none' ? p.MaturityLevel === null : p.MaturityLevel === Number(active),
        );

  /* A process run by two areas appears under both headings rather than forcing
     somebody to nominate one owner — that is why the join is many to many. */
  const groups = new Map<string, Process[]>();
  for (const p of shown) {
    const names = p.areas.length > 0 ? p.areas.map((a) => a.Name) : [UNFILED];
    for (const name of names) {
      const list = groups.get(name);
      if (list) list.push(p);
      else groups.set(name, [p]);
    }
  }
  const ordered = [...groups.keys()].sort(
    (a, b) => Number(a === UNFILED) - Number(b === UNFILED) || a.localeCompare(b),
  );

  return (
    <>
      <PageHead
        title="Processes"
        lede="What the business does, grouped by the function that owns it — with who is accountable for each one, the maturity level it belongs to, and what says it is working."
        actions={
          <button type="button" className="btn btn-primary" onClick={() => setCreating(true)}>
            Add process
          </button>
        }
      />

      {error && <ErrorBanner message={error} />}
      {processes.error && <ErrorBanner message={processes.error} />}
      {processes.loading && !processes.data && <Loading label="Loading processes" />}

      {processes.data && all.length === 0 && (
        <Empty title="No processes yet">
          <p>
            Add the first one. A process is something the business does repeatedly — onboarding, the
            sales cycle, the weekly Level 10 — with a named person accountable for it.
          </p>
        </Empty>
      )}

      {all.length > 0 && (
        <>
          <div className="row-between wrap mb-4">
            <Segmented
              options={filterOptions}
              value={active}
              onChange={setLevelFilter}
              label="Filter by maturity level"
            />
            <span className="xs subtle">
              {shown.length} of {all.length} shown
            </span>
          </div>

          {ordered.map((name) => (
            <section key={name} className="mt-6">
              <div className="row mb-4">
                <h2>{name}</h2>
                <span className="badge badge-neutral">{groups.get(name)?.length}</span>
              </div>
              {/* One row a process rather than a card: the page is read as a list
                  of what each area owns, and a dozen cards a section pushed the
                  next area off the screen. */}
              <ul className="card process-list">
                {groups
                  .get(name)
                  /* Lowest maturity first: the foundations that have to be in
                     place before the level above them means anything. Unrated
                     processes sort last rather than first. */
                  ?.slice()
                  .sort(
                    (a, b) =>
                      (a.MaturityLevel ?? 99) - (b.MaturityLevel ?? 99) ||
                      a.Title.localeCompare(b.Title),
                  )
                  .map((p) => (
                    <ProcessRow
                      key={p.ProcessId}
                      process={p}
                      area={name}
                      onEdit={() => setEditing(p)}
                      onDelete={() => void remove(p)}
                    />
                  ))}
              </ul>
            </section>
          ))}
        </>
      )}

      {(creating || editing) && (
        <ProcessForm
          process={editing}
          areas={areas.data ?? []}
          employees={employees.data ?? []}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSaved={() => {
            setCreating(false);
            setEditing(null);
            processes.reload();
          }}
        />
      )}
    </>
  );
}

function ProcessRow({
  process,
  area,
  onEdit,
  onDelete,
}: {
  process: Process;
  /** The heading this row sits under, left out of its "also" list. */
  area: string;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const others = process.areas.filter((a) => a.Name !== area);
  const count = process.measurables.length;
  return (
    <li className="process-row">
      <div className="process-name">
        <span className="strong" title={process.Description ?? undefined}>
          {process.Title}
        </span>
        {process.Description && <span className="xs muted"> — {process.Description}</span>}
        {others.length > 0 && (
          <span className="xs subtle"> · also {others.map((a) => a.Name).join(', ')}</span>
        )}
      </div>
      <div className="process-level">
        {process.MaturityLevel !== null ? (
          <LevelBadge level={process.MaturityLevel} />
        ) : (
          <span className="xs subtle">Not rated</span>
        )}
      </div>
      <div className="process-owner row">
        {process.OwnerName ? (
          <>
            <Avatar name={process.OwnerName} />
            <span className="xs">{process.OwnerName}</span>
          </>
        ) : (
          <span className="xs subtle">Unassigned</span>
        )}
      </div>
      {/* The measurables themselves are in the tooltip and the edit form; a
          row of pills is what made the card tall. */}
      <span
        className={`process-measurables xs ${count === 0 ? 'subtle' : ''}`}
        title={process.measurables.map((m) => `${m.Name} ${goalLabel(m)}`.trim()).join('\n')}
      >
        {count} {count === 1 ? 'measurable' : 'measurables'}
      </span>
      <div className="process-actions row">
        <button type="button" className="btn btn-ghost btn-sm" onClick={onEdit}>
          Edit
        </button>
        <DeleteButton onConfirm={onDelete} what={`the process “${process.Title}”`} />
      </div>
    </li>
  );
}

/* The description is a sentence saying what the process covers, not a runbook
   — the runbook is the Operations Manual. The column holds the same 256. */
const DESCRIPTION_LIMIT = 256;

function ProcessForm({
  process,
  areas,
  employees,
  onClose,
  onSaved,
}: {
  process: Process | null;
  areas: ProcessArea[];
  employees: Employee[];
  onClose: () => void;
  onSaved: () => void;
}) {
  /* Loaded here rather than by the page: the picker is the only thing that
     reads it, and a modal that opens once a week should not put a request on
     every page load. */
  const options = useResource<ProcessMeasurableOption[]>('/processes/measurable-options');

  const [title, setTitle] = useState(process?.Title ?? '');
  const [description, setDescription] = useState(process?.Description ?? '');
  const [ownerEmployeeId, setOwner] = useState<number | null>(process?.OwnerEmployeeId ?? null);
  /* Held as the select's string. '' is "not rated", which is a real answer
     here rather than an empty field waiting to be filled in. */
  const [level, setLevel] = useState(
    process?.MaturityLevel != null ? String(process.MaturityLevel) : '',
  );
  const [areaIds, setAreaIds] = useState<number[]>(process?.areas.map((a) => a.AreaId) ?? []);
  const [measurables, setMeasurables] = useState<ProcessMeasurable[]>(process?.measurables ?? []);
  const [measurableFilter, setMeasurableFilter] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const picked = (scorecardItemId: number) =>
    measurables.some((m) => m.ScorecardItemId === scorecardItemId);

  /* A ticked measurable always stays visible, even when the filter excludes it
     — otherwise typing a search silently hides what is already attached, and
     the count under the list stops matching what you can see. */
  const needle = measurableFilter.trim().toLowerCase();
  const matchingOptions = (options.data ?? []).filter(
    (o) =>
      needle === '' ||
      picked(o.ScorecardItemId) ||
      o.Name.toLowerCase().includes(needle) ||
      (o.Category ?? '').toLowerCase().includes(needle) ||
      (o.Description ?? '').toLowerCase().includes(needle),
  );

  const toggle = (option: ProcessMeasurableOption) => {
    setMeasurables((current) =>
      picked(option.ScorecardItemId)
        ? current.filter((m) => m.ScorecardItemId !== option.ScorecardItemId)
        : [
            ...current,
            {
              /* Not saved yet, so there is no row id. Negative so it cannot
                 collide with a real one while React keys the list. */
              ProcessMeasurableId: -option.ScorecardItemId,
              ScorecardItemId: option.ScorecardItemId,
              Name: option.Name,
              Unit: option.Unit,
              Goal: option.Goal,
              Comparison: option.Comparison,
            },
          ],
    );
  };

  /* Attached measurables whose library row has gone — or that were attached
     before the library existed, which is the state migration 098 left two of
     them in. Still listed, ticked, so they can be kept or re-picked on purpose
     rather than disappearing off the card without anybody deciding to. */
  const orphaned = measurables.filter(
    (m) =>
      m.ScorecardItemId === null ||
      !(options.data ?? []).some((o) => o.ScorecardItemId === m.ScorecardItemId),
  );

  const available = areas.filter((a) => !areaIds.includes(a.AreaId));

  const submit = async () => {
    if (!title.trim()) {
      setError('A process needs a name.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const body = {
        title: title.trim(),
        description: description.trim() || null,
        ownerEmployeeId,
        maturityLevel: level === '' ? null : Number(level),
        areaIds,
        measurables: measurables.map((m) => ({
          scorecardItemId: m.ScorecardItemId,
          name: m.Name,
          unit: m.Unit,
          goal: m.Goal === null ? null : Number(m.Goal),
          comparison: m.Comparison,
        })),
      };
      if (process) await api.put(`/processes/${process.ProcessId}`, body);
      else await api.post('/processes', body);
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save that process.');
      setSaving(false);
    }
  };

  const remaining = DESCRIPTION_LIMIT - description.length;

  return (
    <Modal
      title={process ? 'Edit process' : 'Add process'}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={saving}
            onClick={() => void submit()}
          >
            {saving ? 'Saving…' : process ? 'Save changes' : 'Add process'}
          </button>
        </>
      }
    >
      {error && <ErrorBanner message={error} />}

      <div className="form-grid">
        <Field label="Process name">
          <input
            className="input"
            autoFocus
            placeholder="Onboarding, Sales cycle, Weekly Level 10…"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
        </Field>
        <Field label="Accountable" hint="Who owns this process running.">
          <EmployeePicker employees={employees} value={ownerEmployeeId} onChange={setOwner} />
        </Field>
        <Field
          label="Maturity level"
          hint="The level at which a business of this maturity should be running it."
        >
          <select className="select" value={level} onChange={(e) => setLevel(e.target.value)}>
            <option value="">Not rated</option>
            {[1, 2, 3, 4, 5].map((n) => (
              <option key={n} value={n}>
                Level {n} — {maturityName(n)}
              </option>
            ))}
          </select>
        </Field>
      </div>

      <div className="field mt-4">
        <label htmlFor="process-description">Description</label>
        <textarea
          id="process-description"
          className="textarea"
          rows={3}
          maxLength={DESCRIPTION_LIMIT}
          placeholder="Briefly, what does this process cover?"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
        <span className="xs subtle right">{remaining} characters remaining</span>
      </div>

      <div className="field mt-4">
        <label htmlFor="process-area">Business areas</label>
        {areaIds.length > 0 && (
          <div className="noc-pills mt-2">
            {areaIds.map((id) => (
              <span key={id} className="noc-pill">
                {areas.find((a) => a.AreaId === id)?.Name ?? `#${id}`}
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  aria-label="Remove area"
                  onClick={() => setAreaIds((ids) => ids.filter((x) => x !== id))}
                >
                  ✕
                </button>
              </span>
            ))}
          </div>
        )}
        <select
          id="process-area"
          className="select"
          value=""
          disabled={available.length === 0}
          onChange={(e) => {
            const id = Number(e.target.value);
            if (id) setAreaIds((ids) => [...ids, id]);
          }}
        >
          <option value="">
            {available.length === 0 ? 'Every area added' : 'Add an area…'}
          </option>
          {available.map((a) => (
            <option key={a.AreaId} value={a.AreaId}>
              {a.Name}
            </option>
          ))}
        </select>
      </div>

      <div className="field mt-4">
        <label htmlFor="measurable-filter">Measurables</label>
        {options.loading && <Loading label="Loading measurables" />}
        {options.data && options.data.length === 0 && orphaned.length === 0 && (
          <p className="xs subtle">
            No scorecard measurables to attach yet. Build a template on the EOS page under
            Templates, then come back and tick the ones this process is judged on.
          </p>
        )}
        {/* A hundred measurables across a dozen templates is too many to scan,
            so the list is filterable and capped. Both halves of the row are
            searched — "Service Desk" finds a template, "CSAT" finds the eight
            measurables of that name across different ones. */}
        {(options.data ?? []).length > 0 && (
          <input
            id="measurable-filter"
            className="input"
            placeholder="Filter by measurable or category…"
            value={measurableFilter}
            onChange={(e) => setMeasurableFilter(e.target.value)}
          />
        )}
        <div className="ui-scroll">
          {matchingOptions.map((o) => (
            <label key={o.ScorecardItemId} className="checkbox mt-2">
              <input
                type="checkbox"
                checked={picked(o.ScorecardItemId)}
                onChange={() => toggle(o)}
              />
              <span className="grow">{o.Name}</span>
              <span className="xs subtle tabular">{goalLabel(o)}</span>
              <span className="xs subtle">{o.Category ?? 'Uncategorised'}</span>
            </label>
          ))}
          {orphaned.map((m) => (
            <label key={m.ProcessMeasurableId} className="checkbox mt-2">
              <input
                type="checkbox"
                checked
                onChange={() =>
                  setMeasurables((current) =>
                    current.filter((x) => x.ProcessMeasurableId !== m.ProcessMeasurableId),
                  )
                }
              />
              <span className="grow">{m.Name}</span>
              <span className="xs subtle tabular">{goalLabel(m)}</span>
              <span className="xs subtle">Not in the library</span>
            </label>
          ))}
        </div>
        {(options.data ?? []).length > 0 && (
          <span className="xs subtle">
            {measurables.length} attached · {matchingOptions.length} of {options.data?.length} shown
          </span>
        )}
      </div>
    </Modal>
  );
}
